import React, { useState, useMemo } from 'react';
import { validate as uuidValidate } from 'uuid';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { GetServerSideProps } from 'next';

// MUI Components
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

// Hooks
import { useUserProfileQuery } from 'src/hooks/__generated__/queries';
import useAppRouter from 'src/hooks/useAppRouter';
import useUser from 'src/hooks/useUser';
import useDynamicLogEvents, { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';

// Components
import CompanyDetailsBlock from 'src/components/company/CompanyDetailsBlock';

import ProfileTopBar from 'src/components/profile/ProfileTopBar';
import HeaderSection from 'src/components/profile/HeaderSection';
import AboutSection from 'src/components/profile/AboutSection';
import ActivitySection from 'src/components/profile/ActivitySection';
import AdsSection from 'src/components/profile/AdsSection';
import RatingSection from 'src/components/profile/RatingSection';
import CommonRelations from 'src/components/profile/CommonRelations';
import RecommendationsSection from 'src/components/profile/recommendations/RecommendationsSection';
import ProjectsSection from 'src/components/profile/ProjectsSection';
import Typography from 'src/components/ui/Typography';
import Loader from 'src/components/ui/Loader';
import FullWidthLayout from 'src/components/other/FullWidthLayout';
import PageNotFound from 'src/pages/404';

// Utils
import type {
    ViewTypes,
    RecommendationModalOpenedType,
} from 'src/components/profile/recommendations/RecommendationsModal';
import RecommendationsModal from 'src/components/profile/recommendations/RecommendationsModal';
import type { Company, User, UserProfileQuery } from 'src/types/__generated__/graphql';
import { ContactStatus, UserProfile } from 'src/types/__generated__/graphql';
import ImagesScroller from 'src/components/ui/ImagesScroller';
import { styledTag } from 'src/utils/styled';
import { getAuthCookie } from 'src/utils/authCookie';
import getApolloClient from 'src/utils/getApolloClient';

import { AnalyticsEvent, getUserOrCompanyOrPagesJaunesProLogParams, EventParamKeys } from 'src/utils/analytics';
import useBreakpoints from 'src/hooks/useBreakpoints';

const ContainerFullWidth = styledTag('div')`
    padding: 0;
`;

const UserProfilePage: PageComponent = () => {
    const router = useAppRouter();
    const userId = router.query.userId as string;
    const { logEventDynamic } = useDynamicLogEvents();

    const currentUser = useUser();
    const isSelf = currentUser?.id === userId;

    const { isMobile, isDesktopLarge } = useBreakpoints();

    const [recommendationModalOpened, setRecommendationModalType] = useState<RecommendationModalOpenedType>(null);
    const [selectedRecommandationsView, setSelectedRecommandationsView] = useState<ViewTypes>('recommendationsList');

    const { data, loading, error } = useUserProfileQuery({
        variables: {
            id: userId,
            showcaseLimit: null,
        },
        skip: !userId || !uuidValidate(userId),
        fetchPolicy: 'cache-and-network',
    });
    const analyticsValue = useMemo(
        () =>
            getUserOrCompanyOrPagesJaunesProLogParams({
                company: { id: userId, name: data?.userProfile?.fullName as string },
            }),
        [data?.userProfile?.fullName, userId],
    );

    if (data && !data.userProfile) {
        return <PageNotFound />;
    }

    const showRightSideInfo = !isSelf || (data?.userProfile?.isProfessionnal ?? data?.userProfile?.companyToDisplay);

    const relationsAndRecommendations = (
        <Grid spacing={isMobile ? 1 : 2} style={{ marginBottom: '0.75rem' }} container>
            {!isSelf && currentUser && data?.userProfile && (
                <Grid xs={12} sm={6} md={6} lg={6} xl={12} item>
                    <CommonRelations user={data.userProfile} />
                </Grid>
            )}
            <Grid xs={12} sm={6} md={6} lg={6} xl={12} item>
                {data?.userProfile?.isProfessionnal && (
                    <RecommendationsSection
                        recommendations={data.recommendations}
                        recommendationsCount={data.userProfile.recommendationsReceivedCount}
                        openRecommendationModal={() => {
                            // cannot rely on context as logEventDynamic is generated outside context
                            logEventDynamic(AnalyticsEvent.SeeRecommendations, {
                                [EventParamKeys.ItemId]: userId,
                                [EventParamKeys.ItemName]: data.userProfile?.fullName ?? undefined,
                            });
                            setRecommendationModalType('fromRecommendations');
                        }}
                    />
                )}
            </Grid>
        </Grid>
    );

    let content: React.ReactElement | null = null;

    if (loading || !data) {
        content = <Loader />;
    }

    if (error) {
        content = (
            <Box my={4}>
                <Typography variant="h2">{error.message}</Typography>
            </Box>
        );
    }

    if (!error && data && data.userProfile && userId) {
        const imagesUrls = data.userProfile.showcase
            ? data.userProfile.showcase.filter((i) => Boolean(i?.url)).map((i) => String(i?.url))
            : undefined;
        content = (
            <AnalyticsContextProvider value={analyticsValue}>
                <Container>
                    <ProfileTopBar />
                </Container>
                <Grid spacing={isDesktopLarge ? 3 : 0} container>
                    <Grid xs={12} sm={12} md={12} lg={12} xl={showRightSideInfo ? 8 : 12} item>
                        {isMobile && (
                            <HeaderSection
                                userProfile={data.userProfile}
                                bottomMargin={!isDesktopLarge && imagesUrls && Boolean(imagesUrls.length)}
                            />
                        )}
                        <Container>
                            <Box mb={6}>
                                {!isMobile && (
                                    <>
                                        <HeaderSection
                                            userProfile={data.userProfile}
                                            bottomMargin={
                                                !isDesktopLarge && !(imagesUrls && Boolean(imagesUrls.length))
                                            }
                                        />
                                        {imagesUrls && Boolean(imagesUrls.length) && (
                                            <Box mb={2} mt="3.5rem">
                                                <ImagesScroller imagesUrls={imagesUrls} />
                                            </Box>
                                        )}
                                    </>
                                )}
                                {!isDesktopLarge && relationsAndRecommendations}
                                {data.userProfile.isRealEstatesTrades ? (
                                    <AdsSection userProfile={data.userProfile} />
                                ) : (
                                    data.userProfile.isProfessionnal && (
                                        <ProjectsSection userProfile={data.userProfile} />
                                    )
                                )}
                                {data.userProfile.isProfessionnal && <Divider />}
                                <AboutSection userProfile={data.userProfile} />
                                <Divider />
                                <ActivitySection userProfile={data.userProfile} />
                                {data.userProfile.isProfessionnal && (
                                    <>
                                        <Divider />
                                        <Box id="rating-section">
                                            <RatingSection
                                                user={data.userProfile}
                                                openRecommendationModal={() => {
                                                    if (!data.userProfile?.recommendationFromMe) {
                                                        setSelectedRecommandationsView('recommendConfirmation');
                                                        setRecommendationModalType('fromRatings');
                                                        return;
                                                    }
                                                    if (data.userProfile.contactStatusV2 === ContactStatus.Uninvited) {
                                                        setSelectedRecommandationsView('addContact');
                                                        setRecommendationModalType('fromRatings');
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Container>
                    </Grid>
                    {isDesktopLarge && (
                        <Grid xs={4} item>
                            {relationsAndRecommendations}
                            {(data.userProfile.companyToDisplay ||
                                (data.userProfile.phoneNumber && data.userProfile.isProfessionnal)) && (
                                <CompanyDetailsBlock
                                    user={data.userProfile as unknown as User}
                                    company={data.userProfile.companyToDisplay as Company}
                                    displayOnCard
                                />
                            )}
                        </Grid>
                    )}
                </Grid>
                <RecommendationsModal
                    user={data.userProfile}
                    recommendationsCount={data.userProfile.recommendationsReceivedCount}
                    modalOpenedType={recommendationModalOpened}
                    setModalType={setRecommendationModalType}
                    recommendationFromMe={data.userProfile.recommendationFromMe}
                    setSelectedView={setSelectedRecommandationsView}
                    selectedView={selectedRecommandationsView}
                />
            </AnalyticsContextProvider>
        );
    }

    const container = isMobile ? (
        <ContainerFullWidth key={data?.userProfile?.id}>{content}</ContainerFullWidth>
    ) : (
        <Container key={data?.userProfile?.id}>{content}</Container>
    );
    return (
        <AnalyticsContextProvider
            value={{
                [EventParamKeys.ItemId]: userId,
                [EventParamKeys.ItemName]: data?.userProfile?.fullName ?? undefined,
            }}
        >
            {container}
        </AnalyticsContextProvider>
    );
};

UserProfilePage.Layout = FullWidthLayout;

export interface UserPageProps extends PageProps {
    user: UserProfileQuery['userProfile'];
}

export const getServerSideProps: GetServerSideProps<UserPageProps> = async (context) => {
    const auth = getAuthCookie(context);
    const apolloClient = getApolloClient(null, auth);
    const userId = context.params?.userId as string | undefined;
    let data;
    if (userId) {
        const result = await apolloClient.query<UserProfileQuery>({
            query: UserProfile,
            variables: {
                id: userId,
            },
        });
        data = result.data;
    }

    const description =
        data?.userProfile?.description && data.userProfile.description.length > 0
            ? data.userProfile.description.substring(0, 200)
            : ' ';
    return {
        props: {
            user: data?.userProfile,
            ...(context.locale
                ? await serverSideTranslations(context.locale, [
                      'common',
                      'profile',
                      'company',
                      'ad',
                      'favorites',
                      'news',
                      'contacts',
                  ])
                : {}),
            SEOProps: {
                pageTitleTranslation: {
                    key: 'common:seo.user.title',
                    params: {
                        name: String(data?.userProfile?.fullName),
                    },
                },
                metaTitleTranslation: {
                    key: 'common:seo.user.title',
                    params: {
                        name: String(data?.userProfile?.fullName),
                    },
                },
                metaDescriptionTranslation: {
                    value: description,
                },
                imageUrl: data?.userProfile?.image?.url ?? null,
            },
        },
    };
};

export default UserProfilePage;
